<template>
  <!-- 当只有一个租户时，不显示下拉菜单 -->
  <el-dropdown
    v-if="showTenantSelect"
    trigger="click"
    :disabled="disabled || loading"
    class="tenant-dropdown"
    @command="handleTenantChange"
  >
    <div class="tenant-trigger">
      <el-icon class="tenant-icon"><office-building /></el-icon>
      <span class="tenant-name">{{ currentTenantName }}</span>
      <el-icon
        class="arrow-icon"
        :class="{ 'is-loading': loading }"
      >
        <arrow-down v-if="!loading" />
        <loading v-else />
      </el-icon>
    </div>
    <template #dropdown>
      <el-dropdown-menu class="tenant-dropdown-menu">
        <div class="tenant-list">
          <el-dropdown-item
            v-for="item in tenantList"
            :key="item.tenantId"
            :command="item.tenantId"
            :class="['tenant-item', { 'is-active': item.tenantId === currentTenantId }]"
          >
            <div class="tenant-item__content">
              <el-icon class="tenant-item__icon"><office-building /></el-icon>
              <span class="tenant-item__name">{{ item.tenantName }}</span>
            </div>
            <el-icon
              v-if="item.tenantId === currentTenantId"
              class="tenant-item__check"
            >
              <check />
            </el-icon>
          </el-dropdown-item>
        </div>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>

<script lang="ts" setup>
  import { ref, computed, onMounted } from 'vue';
  import { ElMessage, ElMessageBox } from 'element-plus';
  import {
    OfficeBuilding, ArrowDown, Check, Loading,
  } from '@element-plus/icons-vue';
  import { LocalStorageService } from '@/utils/storage';
  import { useTenantStore } from '@/store/tenant';

  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['change']);

  const tenantStore = useTenantStore();

  const loading = computed(() => tenantStore.loading);
  const tenantList = computed(() => tenantStore.tenantList);
  const currentTenantId = computed(() => tenantStore.currentTenantId);
  const currentTenantName = computed(() => tenantStore.currentTenantName);

  // 当租户数量大于1时才显示下拉菜单
  const showTenantSelect = computed(() => tenantList.value.length > 1);

  onMounted(async () => {
    try {
      // 初始化时获取租户列表
      await tenantStore.getTenantList();
    } catch (error) {
      console.error('获取租户列表失败:', error);
      ElMessage.error('获取租户列表失败');
    }
  });

  const handleTenantChange = async (tenantId: string) => {
    if (tenantId === currentTenantId.value) { return; }

    try {
      await ElMessageBox.confirm(
        '切换租户将刷新页面，确定要切换吗？',
        '切换租户',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      );

      await tenantStore.switchTenant(tenantId);

      LocalStorageService.set('_current_org_info', {});
      LocalStorageService.set('_current_org_info_list', []);
      LocalStorageService.set('_current_org_ids', []);

      ElMessage.success('切换租户成功');
      emit('change', tenantId);
    } catch (error) {
      // 用户取消不提示错误
      if (error !== 'cancel') {
        console.error('切换租户失败:', error);
        ElMessage.error('切换租户失败');
      }
    }
  };
</script>

<style lang="scss" scoped>
.tenant-dropdown {
  height: 28px;
}

.tenant-trigger {
  display: flex;
  align-items: center;
  height: 28px;
  padding: 0 10px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
  }

  .tenant-icon {
    margin-right: 6px;
    color: #4e7ff1;
    font-size: 16px;
  }

  .tenant-name {
    max-width: 120px;
    overflow: hidden;
    color: #1d2129;
    font-size: 13px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .arrow-icon {
    margin-left: 4px;
    color: #86909c;
    font-size: 12px;
    transition: transform 0.2s ease;

    &.is-loading {
      animation: rotating 1s linear infinite;
    }
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>

<style lang="scss">
// 全局样式，因为 dropdown-menu 是挂载到 body 的
.tenant-dropdown-menu {
  padding: 8px !important;

  .tenant-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .tenant-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-width: 180px;
    height: 40px;
    margin: 0 !important;
    padding: 0 12px !important;
    background: #fff;
    border-radius: 6px;
    transition: all 0.2s ease;

    &__content {
      display: flex;
      align-items: center;
      overflow: hidden;
    }

    &__icon {
      margin-right: 8px;
      color: #86909c;
      font-size: 16px;
      transition: color 0.2s ease;
    }

    &__name {
      overflow: hidden;
      color: #1d2129;
      font-size: 13px;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &__check {
      margin-left: 8px;
      color: #4e7ff1;
      font-size: 14px;
    }

    &:hover {
      background: #f2f5ff;

      .tenant-item__icon {
        color: #4e7ff1;
      }
    }

    &.is-active {
      background: #e8f3ff;

      .tenant-item__name {
        color: #4e7ff1;
        font-weight: 500;
      }

      .tenant-item__icon {
        color: #4e7ff1;
      }
    }
  }
}
</style>
